import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import Image from '@tiptap/extension-image';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';

type Level = 1 | 2 | 3 | 4;

/**
 * TipTap rich-text editor with the same toolbar as the original admin
 * (StarterKit already bundles bold/italic/underline/strike/code/lists/link).
 * Emits HTML, which the API stores and the site renders as-is.
 */
export function RichTextEditor({
  value,
  onChange,
  onUploadImage,
}: {
  value: string;
  onChange: (html: string) => void;
  onUploadImage: (file: File) => Promise<string>;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false } }),
      Image,
      Subscript,
      Superscript,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Write your amazing post here...' }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  const run = (fn: () => unknown) => () => {
    fn();
  };
  const chain = () => editor.chain().focus();
  const cls = (active: boolean) => (active ? 'is-active' : undefined);

  const currentHeading = ([1, 2, 3, 4] as Level[]).find((l) => editor.isActive('heading', { level: l }));

  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previous ?? 'https://');
    if (url === null) return;
    if (url === '') chain().extendMarkRange('link').unsetLink().run();
    else chain().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertImage = async (file: File) => {
    const src = await onUploadImage(file);
    if (src) chain().setImage({ src }).run();
  };

  return (
    <div className="tiptap-wrapper">
      <div className="tiptap-toolbar" id="tiptap-toolbar" role="toolbar" aria-label="Formatting">
        <button type="button" title="Undo" onClick={run(() => chain().undo().run())}><i className="fa fa-undo" /></button>
        <button type="button" title="Redo" onClick={run(() => chain().redo().run())}><i className="fa fa-repeat" /></button>
        <span className="tiptap-sep" />
        <select
          id="tiptap-heading"
          value={currentHeading ?? 'p'}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'p') chain().setParagraph().run();
            else chain().toggleHeading({ level: Number(v) as Level }).run();
          }}
          aria-label="Text style"
        >
          <option value="p">Normal text</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
          <option value="4">Heading 4</option>
        </select>
        <span className="tiptap-sep" />
        <button type="button" title="Bold" className={cls(editor.isActive('bold'))} onClick={run(() => chain().toggleBold().run())}><i className="fa fa-bold" /></button>
        <button type="button" title="Italic" className={cls(editor.isActive('italic'))} onClick={run(() => chain().toggleItalic().run())}><i className="fa fa-italic" /></button>
        <button type="button" title="Underline" className={cls(editor.isActive('underline'))} onClick={run(() => chain().toggleUnderline().run())}><i className="fa fa-underline" /></button>
        <button type="button" title="Strikethrough" className={cls(editor.isActive('strike'))} onClick={run(() => chain().toggleStrike().run())}><i className="fa fa-strikethrough" /></button>
        <button type="button" title="Code" className={cls(editor.isActive('code'))} onClick={run(() => chain().toggleCode().run())}><i className="fa fa-code" /></button>
        <button type="button" title="Subscript" className={cls(editor.isActive('subscript'))} onClick={run(() => chain().toggleSubscript().run())}><i className="fa fa-subscript" /></button>
        <button type="button" title="Superscript" className={cls(editor.isActive('superscript'))} onClick={run(() => chain().toggleSuperscript().run())}><i className="fa fa-superscript" /></button>
        <span className="tiptap-sep" />
        <button type="button" title="Align Left" className={cls(editor.isActive({ textAlign: 'left' }))} onClick={run(() => chain().setTextAlign('left').run())}><i className="fa fa-align-left" /></button>
        <button type="button" title="Align Center" className={cls(editor.isActive({ textAlign: 'center' }))} onClick={run(() => chain().setTextAlign('center').run())}><i className="fa fa-align-center" /></button>
        <button type="button" title="Align Right" className={cls(editor.isActive({ textAlign: 'right' }))} onClick={run(() => chain().setTextAlign('right').run())}><i className="fa fa-align-right" /></button>
        <button type="button" title="Justify" className={cls(editor.isActive({ textAlign: 'justify' }))} onClick={run(() => chain().setTextAlign('justify').run())}><i className="fa fa-align-justify" /></button>
        <span className="tiptap-sep" />
        <button type="button" title="Bullet List" className={cls(editor.isActive('bulletList'))} onClick={run(() => chain().toggleBulletList().run())}><i className="fa fa-list-ul" /></button>
        <button type="button" title="Ordered List" className={cls(editor.isActive('orderedList'))} onClick={run(() => chain().toggleOrderedList().run())}><i className="fa fa-list-ol" /></button>
        <button type="button" title="Blockquote" className={cls(editor.isActive('blockquote'))} onClick={run(() => chain().toggleBlockquote().run())}><i className="fa fa-quote-right" /></button>
        <span className="tiptap-sep" />
        <button type="button" title="Link" className={cls(editor.isActive('link'))} onClick={setLink}><i className="fa fa-link" /></button>
        <label className="tiptap-image-btn" title="Upload Image">
          <i className="fa fa-image" />
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void insertImage(file);
            }}
          />
        </label>
      </div>
      <EditorContent editor={editor} id="tiptap-editor" />
    </div>
  );
}
